import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router";
import DashboardHeader from "../../components/dashboard-header";
import { useUserContext } from "../../hooks/userContext";
import {
  DELETE_HOMEOWNER_PROJECT,
  GET_HOMEOWNER_PROJECTS,
  UPDATE_HOMEOWNER_PROJECT,
} from "../../graphql/homeowner-project";
import type {
  HomeownerProjectRecord,
  HomeownerProjectsQueryData,
  HomeownerProjectsQueryVars,
} from "../homeowner-bio-page/types";
import {
  PROJECT_PROPERTY_TYPE_OPTIONS,
  PROJECT_SCOPE_OPTIONS,
} from "../homeowner-bio-page/data";
import { BUDGET_RANGES } from "../../components/homeowner-preferences-quiz/constants";
import { formatBudgetRangeLabel, formatShortDate, toTitleCase } from "../homeowner-profile/utils";
import { Pages } from "../../utils";
import {
  fetchActiveCollaboration,
  fetchProjectSummary,
  type ActiveCollaborationResponse,
  type ProjectSummaryResponse,
} from "../../services/collaborationApi";
import { useNotification } from "../../contexts/NotificationContext";
import "./styles.scss";

const getOptionLabel = (options: { value: string; label: string }[], value?: string | null) => {
  if (!value) return "";
  const match = options.find((option) => option.value === value || option.label === value);
  return match?.label ?? toTitleCase(value);
};

const getBudgetLabel = (value?: string | null) => {
  if (!value) return "Not provided";
  const match = BUDGET_RANGES.find((option) => option.value === value);
  return formatBudgetRangeLabel(match?.label ?? value);
};

const STATUS_FILTERS = [
  "active",
  "in_progress",
  "paused",
  "completed",
  "draft",
] as const;

type StatusFilter = (typeof STATUS_FILTERS)[number];

const getScopeLabel = (values?: string[] | null) => {
  if (!values?.length) return "Not provided";
  const labels = values
    .map((value) => {
      const match = PROJECT_SCOPE_OPTIONS.find(
        (option) => option.value === value || option.label === value
      );
      return match?.label ?? toTitleCase(value);
    })
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "Not provided";
};

const normalizeSearchValue = (value: string) =>
  value
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeStatus = (value?: string | null) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/\s+/g, "_");
  const allowed = new Set([
    "draft",
    "active",
    "in_progress",
    "paused",
    "completed",
    "cancelled",
  ]);
  return allowed.has(normalized) ? normalized : null;
};

const getStatusLabel = (status: string) => {
  if (status === "in_progress") return "In Progress";
  if (status === "completed") return "Completed";
  if (status === "draft") return "Draft";
  if (status === "paused") return "Paused";
  if (status === "active") return "Active";
  return toTitleCase(status.replace(/_/g, " "));
};

const getProjectStatus = (project: HomeownerProjectRecord) => {
  const normalized = normalizeStatus(project.status);
  if (normalized) return normalized;
  if (project.active === false) {
    return project.archivedAt ? "completed" : "draft";
  }
  return "active";
};

const HomeownerProjectsPage = () => {
  const { user } = useUserContext();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<HomeownerProjectRecord | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<HomeownerProjectRecord | null>(
    null
  );
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [projectSummary, setProjectSummary] = useState<ProjectSummaryResponse | null>(
    null
  );
  const [projectSummaries, setProjectSummaries] = useState<
    Record<string, ProjectSummaryResponse | null>
  >({});
  const [activeCollaboration, setActiveCollaboration] =
    useState<ActiveCollaborationResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilters, setStatusFilters] = useState<StatusFilter[]>([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<"startDate" | "name" | "status">("startDate");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
  const [confirmingCompletionId, setConfirmingCompletionId] = useState<string | null>(
    null
  );
  const [activatingProjectId, setActivatingProjectId] = useState<string | null>(
    null
  );

  const {
    data: projectsData,
    loading,
    error,
  } = useQuery<HomeownerProjectsQueryData, HomeownerProjectsQueryVars>(
    GET_HOMEOWNER_PROJECTS,
    {
      variables: { homeOwnerId: user?.id ?? "" },
      skip: !user?.id,
      fetchPolicy: "cache-and-network",
    }
  );

  const [updateHomeownerProject] = useMutation(UPDATE_HOMEOWNER_PROJECT, {
    refetchQueries: () => {
      if (!user?.id) return [];
      return [
        {
          query: GET_HOMEOWNER_PROJECTS,
          variables: { homeOwnerId: user.id },
        },
      ];
    },
  });

  const [deleteHomeownerProject] = useMutation(DELETE_HOMEOWNER_PROJECT, {
    refetchQueries: () => {
      if (!user?.id) return [];
      return [
        {
          query: GET_HOMEOWNER_PROJECTS,
          variables: { homeOwnerId: user.id },
        },
      ];
    },
  });

  const projects = useMemo(
    () => ((projectsData?.getHomeOwnerProjects ?? []).filter(Boolean) as HomeownerProjectRecord[]),
    [projectsData]
  );

  const hasProjects = projects.length > 0;
  const statusCounts = useMemo(() => {
    const counts = STATUS_FILTERS.reduce(
      (acc, status) => {
        acc[status] = 0;
        return acc;
      },
      {} as Record<StatusFilter, number>
    );
    projects.forEach((project) => {
      const status = getProjectStatus(project) as StatusFilter;
      if (status in counts) counts[status] += 1;
    });
    return counts;
  }, [projects]);

  useEffect(() => {
    if (!user?.id) return;
    fetchActiveCollaboration(user.id, "homeowner")
      .then((data) => setActiveCollaboration(data))
      .catch(() => setActiveCollaboration(null));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !projects.length) return;
    const completedProjects = projects.filter(
      (project) => getProjectStatus(project) === "completed" && project._id
    );
    const missingSummaries = completedProjects.filter(
      (project) => project._id && !projectSummaries[project._id]
    );
    if (!missingSummaries.length) return;

    missingSummaries.forEach((project) => {
      if (!project._id) return;
      fetchProjectSummary(project._id, user.id)
        .then((summary) => {
          setProjectSummaries((prev) => ({ ...prev, [project._id as string]: summary }));
        })
        .catch(() => {
          setProjectSummaries((prev) => ({ ...prev, [project._id as string]: null as any }));
        });
    });
  }, [projects, projectSummaries, user?.id]);

  const filteredProjects = useMemo(() => {
    const normalizedSearch = normalizeSearchValue(searchTerm);
    const activeStatusFilters = statusFilters.length
      ? statusFilters
      : STATUS_FILTERS;

    const base = projects.filter((project) => {
      const status = getProjectStatus(project) as StatusFilter;
      if (!activeStatusFilters.includes(status)) return false;

      if (!normalizedSearch) return true;
      const summary = project._id ? projectSummaries[project._id] : null;
      const propertyTypeLabel = getOptionLabel(
        PROJECT_PROPERTY_TYPE_OPTIONS,
        project.propertyType ?? ""
      );
      const budgetLabel = getBudgetLabel(project.budgetRange);
      const statusLabel = getStatusLabel(status);
      const designerName =
        summary?.designer?.name ||
        summary?.designer?.aliasId ||
        activeCollaboration?.designer?.name ||
        activeCollaboration?.designer?.aliasId ||
        "";
      const scopeLabel = getScopeLabel(project.projectScope);
      const haystack = normalizeSearchValue(
        [
          project.projectName || "Untitled Project",
          designerName,
          scopeLabel,
          propertyTypeLabel,
          project.propertyType,
          budgetLabel,
          project.propertyLocation,
          statusLabel,
          status,
        ]
          .filter(Boolean)
          .join(" ")
      );
      return haystack.includes(normalizedSearch);
    });

    const sorted = [...base].sort((a, b) => {
      if (sortBy === "name") {
        const nameA = (a.projectName || "").toLowerCase();
        const nameB = (b.projectName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      }
      if (sortBy === "status") {
        const order: Record<string, number> = {
          active: 0,
          in_progress: 1,
          paused: 2,
          draft: 3,
          completed: 4,
        };
        return (order[getProjectStatus(a)] ?? 99) - (order[getProjectStatus(b)] ?? 99);
      }
      const dateA = a.projectStartDate ? Date.parse(a.projectStartDate) : 0;
      const dateB = b.projectStartDate ? Date.parse(b.projectStartDate) : 0;
      return dateA - dateB;
    });

    return sortOrder === "desc" ? sorted.reverse() : sorted;
  }, [
    activeCollaboration?.designer?.aliasId,
    activeCollaboration?.designer?.name,
    projectSummaries,
    projects,
    searchTerm,
    sortBy,
    sortOrder,
    statusFilters,
  ]);

  const filterMenuOpen = Boolean(filterAnchorEl);
  const handleOpenFilterMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };
  const handleCloseFilterMenu = () => setFilterAnchorEl(null);

  const handleToggleStatusFilter = (status: StatusFilter) => {
    setStatusFilters((prev) => {
      const next = prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status];
      return STATUS_FILTERS.filter((item) => next.includes(item));
    });
  };

  const handleClearFilters = () => {
    setStatusFilters([]);
  };

  const handleConfirmCompletion = async (projectId?: string | null) => {
    if (!projectId) return;
    setConfirmingCompletionId(projectId);
    try {
      await updateHomeownerProject({
        variables: {
          _id: projectId,
          status: "completed",
          completionConfirmedBy: "homeowner",
        },
      });
      showSuccess("Project marked complete.");
    } catch {
      showError("Unable to complete the project. Please try again.");
    } finally {
      setConfirmingCompletionId(null);
    }
  };

  const handleActivateProject = async (projectId?: string | null) => {
    if (!projectId) return;
    setActivatingProjectId(projectId);
    try {
      await updateHomeownerProject({
        variables: {
          _id: projectId,
          status: "active",
        },
      });
      showSuccess("Project activated.");
    } catch {
      showError("Unable to activate the project. Please try again.");
    } finally {
      setActivatingProjectId(null);
    }
  };

  const handleOpenDetails = async (project: HomeownerProjectRecord) => {
    if (!project?._id) return;
    setSelectedProject(project);
    const cachedSummary = projectSummaries[project._id] ?? null;
    setProjectSummary(cachedSummary);
    setDetailsError(null);
    setDetailsOpen(true);

    if (!user?.id) return;
    if (cachedSummary) return;
    setDetailsLoading(true);
    try {
      const summary = await fetchProjectSummary(project._id, user.id);
      setProjectSummary(summary);
    } catch {
      setDetailsError("Unable to load project details.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedProject(null);
    setProjectSummary(null);
    setDetailsError(null);
  };

  const handleOpenDeleteDialog = (project: HomeownerProjectRecord) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleDeleteDraftProject = async () => {
    const projectId = projectToDelete?._id;
    if (!projectId) return;
    setDeletingProjectId(projectId);
    try {
      const response = await deleteHomeownerProject({
        variables: { _id: projectId },
      });
      if (!response.data?.deleteHomeOwnerProject) {
        throw new Error("Delete failed");
      }
      showSuccess("Draft project deleted.");
      handleCloseDeleteDialog();
    } catch {
      showError("Unable to delete the draft project. Please try again.");
    } finally {
      setDeletingProjectId(null);
    }
  };

  return (
    <Box className="homeowner-projects-page">
      <DashboardHeader
        title="All projects"
        primaryAction={{
          label: "Create Project",
          onClick: () => navigate(Pages.MY_PROJECTS_CREATE),
          variant: "contained",
          color: "primary",
        }}
      />

      <Box className="homeowner-projects-body">
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: "#00A06D" }} />
          </Box>
        )}

        {error && (
          <Alert severity="error">
            Unable to load projects. Please refresh and try again.
          </Alert>
        )}

        {!loading && !error && !hasProjects && (
          <Paper className="projects-empty-card" elevation={0}>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Typography variant="h5" fontWeight={700}>
                No projects yet
              </Typography>
              <Typography color="text.secondary">
                Create your first project to start receiving designer matches. Each
                project can have its own location, scope, budget, and timeline.
              </Typography>
              <Alert severity="info" sx={{ width: "100%" }}>
                Your Matching Profile captures your design preferences. Projects
                capture the details for each home you want to design.
              </Alert>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(Pages.MY_PROJECTS_CREATE)}
              >
                Create Your First Project
              </Button>
            </Stack>
          </Paper>
        )}

        {!loading && !error && hasProjects && (
          <Stack spacing={2}>
            <Box className="projects-toolbar">
              <TextField
                size="small"
                placeholder="Search by project or designer"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="projects-search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" className="projects-search-icon" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                className="projects-filter-button"
                startIcon={<TuneIcon fontSize="small" />}
                onClick={handleOpenFilterMenu}
                aria-haspopup="true"
                aria-expanded={filterMenuOpen ? "true" : undefined}
              >
                Filters
              </Button>
              <Menu
                anchorEl={filterAnchorEl}
                open={filterMenuOpen}
                onClose={handleCloseFilterMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { minWidth: 240 } }}
              >
                <Box px={2} pt={1.5} pb={0.5}>
                  <Typography variant="subtitle2">Filter by status</Typography>
                </Box>
                <Divider />
                {STATUS_FILTERS.map((status) => {
                  const label = `${getStatusLabel(status)} (${statusCounts[status]})`;
                  const isChecked = statusFilters.includes(status);
                  return (
                    <MenuItem key={status} onClick={() => handleToggleStatusFilter(status)}>
                      <ListItemIcon>
                        <Checkbox size="small" checked={isChecked} />
                      </ListItemIcon>
                      <ListItemText primary={label} />
                    </MenuItem>
                  );
                })}
                <Divider />
                <MenuItem
                  onClick={() => {
                    handleClearFilters();
                    handleCloseFilterMenu();
                  }}
                >
                  <ListItemText primary="Clear filters" />
                </MenuItem>
              </Menu>
              <Box className="projects-sort">
                <Typography variant="body2">Sort by</Typography>
                <TextField
                  select
                  size="small"
                  value={sortBy}
                  onChange={(event) =>
                    setSortBy(event.target.value as "startDate" | "name" | "status")
                  }
                  className="projects-sort-select"
                >
                  <MenuItem value="startDate">Start date</MenuItem>
                  <MenuItem value="name">Project name</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  value={sortOrder}
                  onChange={(event) =>
                    setSortOrder(event.target.value as "asc" | "desc")
                  }
                  className="projects-sort-select"
                >
                  <MenuItem value="desc">Descending</MenuItem>
                  <MenuItem value="asc">Ascending</MenuItem>
                </TextField>
                <Typography variant="body2" className="projects-total">
                  {filteredProjects.length} total
                </Typography>
              </Box>
            </Box>
            {statusFilters.length > 0 && (
              <Box className="projects-filter-row">
                {STATUS_FILTERS.filter((status) => statusFilters.includes(status)).map(
                  (status) => {
                    const label = `${getStatusLabel(status)} (${statusCounts[status]})`;
                    const isActive = statusFilters.includes(status);
                    return (
                      <Chip
                        key={status}
                        label={label}
                        onClick={() =>
                          setStatusFilters((prev) =>
                            prev.includes(status)
                              ? prev.filter((item) => item !== status)
                              : [...prev, status]
                          )
                        }
                        className={`projects-filter-chip ${isActive ? "active" : ""}`}
                      />
                    );
                  }
                )}
                <Button
                  className="projects-clear-button"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </Button>
              </Box>
            )}
            <Stack spacing={2}>
              {filteredProjects.map((project) => {
                const propertyTypeLabel = getOptionLabel(
                  PROJECT_PROPERTY_TYPE_OPTIONS,
                  project.propertyType ?? ""
                );
                const scopeLabel = getScopeLabel(project.projectScope);
                const budgetLabel = getBudgetLabel(project.budgetRange);
                const startDateLabel = project.projectStartDate
                  ? formatShortDate(project.projectStartDate)
                  : "Not provided";
                const status = getProjectStatus(project);
                const statusLabel = getStatusLabel(status);
                const summary = project._id ? projectSummaries[project._id] : null;
                const designerName =
                  summary?.designer?.name ||
                  summary?.designer?.aliasId ||
                  activeCollaboration?.designer?.name ||
                  "Designer pending";
                const designerAvatar =
                  status === "active"
                    ? activeCollaboration?.designer?.profilePicUrl || ""
                    : summary?.designer?.profilePicUrl || "";
                const ratingValue = summary?.feedback?.rating ?? 0;
                const ratingLabel =
                  ratingValue && ratingValue > 0 ? ratingValue.toFixed(1) : "—";
                const completionPending =
                  Boolean(project.completionRequestedBy) &&
                  !project.completionConfirmedAt &&
                  project.status !== "completed";
                const pendingByDesigner = project.completionRequestedBy === "designer";

                const isDraft = status === "draft";
                return (
                  <Paper key={project._id ?? project.projectName} className="project-list-item" elevation={0}>
                    <Box className="project-item-main">
                      <Box className="project-item-title-row">
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            {project.projectName || "Untitled Project"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {propertyTypeLabel || "Property type not set"}
                          </Typography>
                        </Box>
                        <Box className="project-item-badges">
                          {completionPending ? (
                            <Chip
                              label="Complete Requested"
                              className="project-complete-requested-chip"
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                          <Chip
                            label={statusLabel}
                            className={`project-status-chip ${status}`}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      </Box>

                      <Box className="project-item-meta">
                        <Box className="project-designer">
                          <Box className="project-avatar" style={{ backgroundImage: `url(${designerAvatar})` }}>
                            {!designerAvatar && designerName.charAt(0)}
                          </Box>
                          <Box>
                            <Typography className="project-designer-name">
                              {designerName}
                            </Typography>
                            <Typography className="project-designer-sub">
                              {status === "active"
                                ? "Active collaboration"
                                : status === "completed"
                                  ? "Completed project"
                                  : "Awaiting match"}
                            </Typography>
                          </Box>
                        </Box>
                        {status === "completed" && (
                          <Box className="project-rating">
                            <Rating
                              value={ratingValue || 0}
                              precision={0.5}
                              readOnly
                              size="small"
                            />
                            <span className="project-rating-value">{ratingLabel}</span>
                          </Box>
                        )}
                      </Box>

                      <Box className="project-item-info">
                        <span>Scope: {scopeLabel}</span>
                        <span>Budget: {budgetLabel}</span>
                        <span>Start: {startDateLabel}</span>
                      </Box>
                    </Box>

                    <Box className="project-item-actions">
                      {completionPending && pendingByDesigner ? (
                        <Button
                          variant="contained"
                          className="project-complete-btn"
                          onClick={() => handleConfirmCompletion(project._id)}
                          disabled={confirmingCompletionId === project._id}
                        >
                          {confirmingCompletionId === project._id
                            ? "Updating..."
                            : "Mark as Complete"}
                        </Button>
                      ) : status === "completed" ? (
                        <Button
                          variant="contained"
                          className="project-view-btn"
                          onClick={() => handleOpenDetails(project)}
                        >
                          View Details
                        </Button>
                      ) : status === "draft" || status === "paused" ? (
                        <Stack direction="row" spacing={1} className="project-action-row">
                          <Button
                            variant="contained"
                            className="project-activate-btn"
                            onClick={() => handleActivateProject(project._id)}
                            disabled={activatingProjectId === project._id}
                          >
                            {activatingProjectId === project._id
                              ? "Activating..."
                              : "Make Active"}
                          </Button>
                          <Button
                            variant="outlined"
                            className="project-edit-btn"
                            onClick={() => {
                              if (!project._id) return;
                              navigate(
                                Pages.MY_PROJECTS_EDIT.replace(":projectId", project._id)
                              );
                            }}
                          >
                            Edit Project
                          </Button>
                          {isDraft ? (
                            <Button
                              variant="outlined"
                              className="project-delete-btn"
                              onClick={() => handleOpenDeleteDialog(project)}
                              disabled={deletingProjectId === project._id}
                            >
                              {deletingProjectId === project._id
                                ? "Deleting..."
                                : "Delete Draft"}
                            </Button>
                          ) : null}
                        </Stack>
                      ) : (
                        <Button
                          variant="outlined"
                          className="project-edit-btn"
                          onClick={() => {
                            if (!project._id) return;
                            navigate(
                              Pages.MY_PROJECTS_EDIT.replace(":projectId", project._id)
                            );
                          }}
                        >
                          Edit Project
                        </Button>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        )}
      </Box>

      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        className="project-details-dialog"
      >
        <DialogTitle>Project Details</DialogTitle>
        <DialogContent>
          {selectedProject ? (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {selectedProject.projectName || "Untitled Project"}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {getOptionLabel(
                    PROJECT_PROPERTY_TYPE_OPTIONS,
                    selectedProject.propertyType ?? ""
                  ) || "Property type not set"}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Project Summary
                </Typography>
                <Box className="project-detail-grid">
                  <Box className="project-detail-item">
                    <Typography variant="caption" color="text.secondary">
                      Project Scope
                    </Typography>
                    <Typography fontWeight={600}>
                      {getScopeLabel(selectedProject.projectScope)}
                    </Typography>
                  </Box>
                  <Box className="project-detail-item">
                    <Typography variant="caption" color="text.secondary">
                      Budget Range
                    </Typography>
                    <Typography fontWeight={600}>
                      {getBudgetLabel(selectedProject.budgetRange)}
                    </Typography>
                  </Box>
                  <Box className="project-detail-item">
                    <Typography variant="caption" color="text.secondary">
                      Preferred Start
                    </Typography>
                    <Typography fontWeight={600}>
                      {selectedProject.projectStartDate
                        ? formatShortDate(selectedProject.projectStartDate)
                        : "Not provided"}
                    </Typography>
                  </Box>
                  <Box className="project-detail-item">
                    <Typography variant="caption" color="text.secondary">
                      Property Location
                    </Typography>
                    <Typography fontWeight={600}>
                      {selectedProject.propertyLocation || "Not provided"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Designer
                </Typography>
                {detailsLoading ? (
                  <Typography color="text.secondary">Loading designer...</Typography>
                ) : (
                  <Typography fontWeight={600}>
                    {projectSummary?.designer?.name ||
                      projectSummary?.designer?.aliasId ||
                      "Designer not assigned"}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Review
                </Typography>
                {detailsLoading ? (
                  <Typography color="text.secondary">Loading review...</Typography>
                ) : projectSummary?.feedback ? (
                  <Stack spacing={1}>
                    <Rating
                      value={projectSummary.feedback.rating ?? 0}
                      precision={0.5}
                      readOnly
                    />
                    {projectSummary.feedback.review ? (
                      <Typography>{projectSummary.feedback.review}</Typography>
                    ) : (
                      <Typography color="text.secondary">
                        No written review provided.
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No review submitted yet.</Typography>
                )}
              </Box>

              {detailsError && <Alert severity="error">{detailsError}</Alert>}
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete draft project?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This will permanently remove{" "}
            <strong>{projectToDelete?.projectName || "this draft project"}</strong>
            . This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={Boolean(deletingProjectId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteDraftProject}
            disabled={Boolean(deletingProjectId)}
          >
            {deletingProjectId ? "Deleting..." : "Delete Draft"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeownerProjectsPage;
